import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../../hooks/useDebounce";
import useI18n from "../../../../../../hooks/useTranslations";
import { Shuttle } from "../../../../../../http/patrol/types/shuttles";
import { AdminSystemType } from "../../../../../../http/patrol/types/systemTypes";
import { usePatrolSystemsApi } from "../../../../../../http/patrol/usePatrolSystems";
import { useShuttlesApi } from "../../../../../../http/patrol/useShuttles";
import { usePatrolSteelVersionsApi } from "../../../../../../http/patrol/useSteelVersion";
import { useSystemTypesApi } from "../../../../../../http/patrol/useSystemTypes";
import { SearchAdminItem } from "../../../../../../http/types/items";
import { useItemsApi } from "../../../../../../http/useItems";
import Button from "../../../../../ui/Button";
import Form from "../../../../../ui/Form";
import FormField from "../../../../../ui/FormField";
import FormFieldsContainer from "../../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../../ui/MultiCheckbox";
import Switch from "../../../../../ui/Switch";
import VisibilityScope, { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";
import { CustomAsyncSelect, CustomSelect } from "../../../../../ui/select/CustomSelect";

interface ShuttleFormBody {
  item: SearchAdminItem | null;
  systemType: AdminSystemType | null;
  fixed: boolean;
  visibility: VisibilityScopeValue;
  systemIds: string[] | null;
  suportMaterialIds: string[] | null;
}

interface ShuttleFormProps {
  defaultValues?: Shuttle;
  onClose: () => void;
}

const ShuttleForm: FC<ShuttleFormProps> = ({ onClose, defaultValues }) => {
  const { patrolSystemTypeId } = useParams<{ patrolSystemTypeId: string }>();
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const itemsApi = useItemsApi();
  const shuttlesApi = useShuttlesApi();
  const patrolSystemTypesApi = useSystemTypesApi();
  const systemsApi = usePatrolSystemsApi();
  const supportMaterialsApi = usePatrolSteelVersionsApi();

  const patrolSystemTypesResult = patrolSystemTypesApi.useAdminSystemTypes({}, aborter.current.signal);
  const systemsResult = systemsApi.useAdminPatrolSystems({ limit: 100 }, aborter.current.signal);
  const supportMaterials = supportMaterialsApi.useAdminMaterials({}, aborter.current.signal);

  return (
    <Form<ShuttleFormBody>
      defaultValues={{ item: null, visibility: "hidden", fixed: false }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        const common = {
          systemTypeId: patrolSystemTypeId ?? formData.systemType?.id!,
          fixed: formData.fixed,
          visibility: formData.visibility,
          systemIds: formData.systemIds ?? [],
          supportMaterialIds: formData.suportMaterialIds ?? [],
        };
        setIsSubmitting(true);
        if (defaultValues?.id) {
          try {
            await shuttlesApi.updateAdminShuttle(defaultValues.id, common);
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        } else {
          try {
            await shuttlesApi.insertAdminShuttle({
              ...common,
              itemId: formData.item?.id!,
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, setValue, watch, reset }) => {
        const fixed = !!watch("fixed");
        const systemIds = watch("systemIds") ?? [];
        const suportMaterialIds = watch("suportMaterialIds") ?? [];

        useEffect(() => {
          defaultValues &&
            reset({
              ...defaultValues,
              systemIds: defaultValues.systems?.map((x) => x.id),
              suportMaterialIds: defaultValues.supportMaterials?.map((x) => x.id),
            });
        }, [defaultValues]);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={(x) => x.code}
                            isDisabled={!!defaultValues?.id}
                            getOptionValue={(x) => x.id}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="systemType"
              control={control}
              rules={{ required: !patrolSystemTypeId }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.patrolSystems.singular}
                  error={fieldState.error}
                  hidden={!!patrolSystemTypeId}
                >
                  {({ labelId }) => (
                    <CustomSelect<AdminSystemType>
                      {...field}
                      inputId={labelId}
                      options={patrolSystemTypesResult.data?.items ?? []}
                      isDisabled={!!patrolSystemTypesResult.error || isSubmitting}
                      isLoading={patrolSystemTypesResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.fixed} isOptional>
              {({ labelId }) => (
                <Switch id={labelId} checked={fixed} onChange={(checked) => setValue("fixed", checked)} />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.materials.plural}>
              <MultiCheckbox
                values={
                  supportMaterials.data?.items.map((x) => ({
                    id: x.id,
                    title: x.name,
                    selected: suportMaterialIds.includes(x.id),
                  })) ?? []
                }
                onUpdate={(id, checked) => {
                  setValue(
                    "suportMaterialIds",
                    checked ? [...suportMaterialIds, id] : suportMaterialIds.filter((x) => x !== id),
                  );
                }}
              />
            </FormField>
            <FormField label={i18n.translation.systems.plural}>
              <MultiCheckbox
                values={
                  systemsResult.data?.items
                    .filter((x) => x.isLifeLine)
                    .map((x) => ({
                      id: x.id,
                      title: x.name,
                      selected: systemIds.includes(x.id),
                    })) ?? []
                }
                onUpdate={(id, checked) => {
                  setValue("systemIds", checked ? [...systemIds, id] : systemIds.filter((x) => x !== id));
                }}
              />
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default ShuttleForm;
